import { useMemo, useRef, useState } from "react";
import { api, Contact, CREDIT_USD, creditCost } from "../lib/api";

interface Props {
  contact: Contact;
  onClose: () => void;
  onPicked: (newPrimary: string, newStatus: Contact["verification_status"]) => void;
  onTested?: (status: Contact["verification_status"]) => void;
}

type NBStatus = "valid" | "invalid" | "catchall" | "disposable" | "unknown" | "error";

interface CandidateState {
  email: string;
  origin: "primary" | "suggested" | "manual";
  status?: NBStatus;
  requestId?: string | null;
  raw?: unknown;
  busy?: boolean;
  error?: string;
  fromRecord?: boolean;      // status came from the DB, not from a check run in this modal
  cached?: boolean;          // server returned an answer already paid for — no credit spent
}

// NeverBounce's credits_info block, requested by /verify-one.
interface CreditsInfo {
  free_credits_remaining?: number;
  paid_credits_remaining?: number;
}

function parseAlts(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw);
    if (Array.isArray(p)) return p.map(String).map(s => s.trim().toLowerCase()).filter(s => s.includes("@"));
  } catch { /* fall through */ }
  return String(raw).split(/[,;\s\n]+/).map(s => s.trim().toLowerCase()).filter(s => s.includes("@"));
}

const NB_STATUSES = new Set<string>(["valid", "invalid", "catchall", "disposable", "unknown"]);

const MEANING: Record<NBStatus, string> = {
  valid:      "Mailbox confirmed to exist — safe to send.",
  invalid:    "Mailbox does not exist — do not send.",
  catchall:   "Domain accepts all addresses; this exact mailbox can't be confirmed. Best NeverBounce can do for catch-all domains — decide manually.",
  disposable: "Temporary/throwaway address.",
  unknown:    "Mail server didn't give a definitive answer (greylisting/timeout). Re-test later or decide manually.",
  error:      "The check failed — see the message below.",
};

export default function VerifyCandidatesModal({ contact, onClose, onPicked, onTested }: Props) {
  const initial = useMemo<CandidateState[]>(() => {
    const list: CandidateState[] = [];
    const seen = new Set<string>();
    // If NeverBounce already answered for this contact's primary, carry that in so
    // reopening the modal doesn't invite paying for the same address again.
    const onRecord = contact.verification_provider === "neverbounce" && NB_STATUSES.has(contact.verification_status)
      ? (contact.verification_status as NBStatus)
      : undefined;
    if (contact.email) {
      const e = contact.email.trim().toLowerCase();
      if (e.includes("@")) {
        list.push({ email: e, origin: "primary", status: onRecord, fromRecord: !!onRecord });
        seen.add(e);
      }
    }
    for (const a of parseAlts(contact.alt_emails)) {
      if (!seen.has(a)) { list.push({ email: a, origin: "suggested" }); seen.add(a); }
    }
    return list;
  }, [contact]);

  const [rows, setRows]   = useState<CandidateState[]>(initial);
  const [manual, setManual] = useState("");
  const [busySave, setBusySave] = useState<string | null>(null);
  const [spent, setSpent] = useState(0);                              // credits burned in this modal
  const [balance, setBalance] = useState<number | null>(null);        // NeverBounce's own figure

  const domainOf = (email: string) => email.split("@")[1] || "";

  // "catchall" and "unknown" are properties of the DOMAIN, not the mailbox. Once a
  // domain has answered either way, no other address there can be confirmed — testing
  // the remaining name guesses only spends credits. This is where the money went.
  const domainVerdict = new Map<string, NBStatus>();
  for (const r of rows) {
    if (r.status === "catchall" || r.status === "unknown") {
      const d = domainOf(r.email);
      // catchall is the firmer verdict — don't let a later unknown downgrade it
      if (d && domainVerdict.get(d) !== "catchall") domainVerdict.set(d, r.status);
    }
  }
  function blockedBy(r: CandidateState): NBStatus | undefined {
    if (r.status) return undefined;                 // already has its own answer
    return domainVerdict.get(domainOf(r.email));
  }

  // One check at a time. Three Test buttons clicked in a second is three credits
  // spent before the first answer is even on screen. The ref closes the gap where
  // two clicks land in the same frame and both read the old state.
  const anyBusy = rows.some(r => r.busy);
  const inFlight = useRef(false);

  async function test(idx: number) {
    const row = rows[idx];
    if (!row || row.busy || anyBusy || inFlight.current) return;

    const blocker = blockedBy(row);
    if (blocker === "catchall") return;             // button is disabled; belt and braces
    if (blocker === "unknown" && !confirm(
      `${domainOf(row.email)} already answered "unknown" for another address.\n\n` +
      `That is usually the mail server refusing to answer at all, so this check will ` +
      `probably return "unknown" too.\n\n` +
      `COST: ${creditCost(1)} either way.\n\nTest anyway?`
    )) return;

    // A deliberate re-test has to say so, otherwise the server hands back the answer
    // it already holds for this address rather than paying for it again.
    const deliberateRetest = !!row.status;
    if (deliberateRetest && !confirm(
      `${row.email} was already checked and came back "${row.status}".\n\n` +
      `Re-testing normally returns the same answer.\n\n` +
      `COST: another ${creditCost(1)}.\n\nRe-test anyway?`
    )) return;

    inFlight.current = true;
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, busy: true, error: undefined } : r));
    try {
      const res = await api.verifyOne(row.email, contact.id, deliberateRetest);
      if (!res.cached) setSpent(n => n + 1);
      const info = (res.raw as { credits_info?: CreditsInfo } | undefined)?.credits_info;
      if (info) {
        const left = (info.paid_credits_remaining ?? 0) + (info.free_credits_remaining ?? 0);
        setBalance(left);
      }
      const status = (res.result as NBStatus) || "unknown";
      setRows(prev => prev.map((r, i) => i === idx
        ? { ...r, busy: false, status, requestId: res.requestId ?? null, raw: res.raw, error: res.error, fromRecord: false, cached: !!res.cached }
        : r));
      try {
        await api.update(contact.id, {
          verification_status: status as Contact["verification_status"],
          verification_provider: "neverbounce",
        });
        onTested?.(status as Contact["verification_status"]);
      } catch { /* result still shown even if the save fails */ }
    } catch (e) {
      setRows(prev => prev.map((r, i) => i === idx
        ? { ...r, busy: false, status: "error", error: e instanceof Error ? e.message : String(e) }
        : r));
    } finally {
      inFlight.current = false;
    }
  }

  function removeRow(idx: number) {
    setRows(prev => prev.filter((_, i) => i !== idx));
  }
  function addManual() {
    const e = manual.trim().toLowerCase();
    if (!e.includes("@")) { alert("Enter a valid email"); return; }
    if (rows.some(r => r.email === e)) { alert("Already in the list"); return; }
    setRows(prev => [...prev, { email: e, origin: "manual" }]);
    setManual("");
  }

  async function approve(idx: number) {
    const r = rows[idx];
    const status = r.status ?? "unknown";
    // Keep the addresses that weren't picked. Deleting them threw away work that had
    // already been paid for, and guaranteed they'd be regenerated and re-checked later.
    const keep = rows.filter((_, i) => i !== idx).map(x => x.email);
    if (!confirm(
      `Save ${r.email} as the contact's primary email and mark "${status}"?` +
      (keep.length ? `\n\n${keep.length} other candidate(s) stay on the contact.` : "")
    )) return;
    setBusySave(r.email);
    try {
      await api.update(contact.id, {
        email: r.email,
        alt_emails: keep.length ? JSON.stringify(keep) : null,
        verification_status: status as Contact["verification_status"],
        verification_provider: r.status && !r.fromRecord ? "neverbounce" : contact.verification_provider ?? null,
      });
      onPicked(r.email, status as Contact["verification_status"]);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally { setBusySave(null); }
  }

  const untested = rows.filter(r => !r.status && !blockedBy(r)).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onMouseDown={(e) => { if (e.button === 0 && e.target === e.currentTarget) onClose(); }}
    >
      <div className="card w-full max-w-2xl p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-tightish text-slate-900">Verify emails — {contact.name}</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {contact.company || "—"} · Each <strong>Test</strong> spends <strong>1 credit (USD ${CREDIT_USD})</strong> on one address.
              Most rows below are generated guesses at the same person's address — testing all
              {rows.length > 1 ? ` ${rows.length}` : ""} costs {creditCost(Math.max(rows.length, 1))} and only one can be real.
            </p>
          </div>
          <button className="btn-ghost !text-xs !py-1 !px-2" onClick={onClose}>Close</button>
        </div>

        {/* Running cost — the app used to show nothing at all here. */}
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-slate-50 px-3 py-2 text-[11px]">
          <span className="text-slate-600">
            Spent on this contact: <strong className={spent > 1 ? "text-amber-700" : "text-slate-800"}>{spent}</strong>
            {" "}<span className={spent > 1 ? "text-amber-700" : "text-slate-500"}>(USD ${(spent * CREDIT_USD).toFixed(3)})</span>
          </span>
          <span className="text-slate-600">
            Balance left: <strong className="text-slate-800">{balance == null ? "—" : balance.toLocaleString()}</strong>
            {balance != null && <span className="text-slate-500"> (USD ${(balance * CREDIT_USD).toFixed(2)})</span>}
          </span>
          <span className="ml-auto text-slate-500">
            {untested} address(es) still testable · {rows.length - untested} settled
          </span>
        </div>

        <div className="space-y-2">
          {rows.length === 0 && (
            <div className="rounded bg-slate-50 px-3 py-3 text-xs text-slate-600">
              No candidates left. Use the manual entry below to add a known address.
            </div>
          )}
          {rows.map((r, i) => {
            const blocker = blockedBy(r);
            const hardBlocked = blocker === "catchall";
            const testDisabled = !!r.busy || anyBusy || hardBlocked;
            return (
              <div key={r.email} className={`rounded-md border px-3 py-2 ${hardBlocked ? "border-slate-200 bg-slate-50/60" : "border-slate-200"}`}>
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className={`truncate font-mono text-[12px] ${hardBlocked ? "text-slate-400" : "text-slate-800"}`}>{r.email}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-500">
                      <OriginBadge origin={r.origin} />
                      {r.status && <ResultBadge result={r.status} />}
                      {r.fromRecord && <span className="text-slate-400">already on record</span>}
                      {r.cached && <span className="text-emerald-700">reused — no credit spent</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => test(i)}
                    disabled={testDisabled}
                    className="btn-secondary !text-xs !py-1 !px-2.5 disabled:opacity-40"
                    title={
                      hardBlocked ? `${domainOf(r.email)} is catch-all — no address there can be confirmed, so this would spend a credit for nothing`
                      : anyBusy && !r.busy ? "Wait for the running check to finish"
                      : r.status ? "Already answered — re-testing costs another credit"
                      : "Run NeverBounce on this address (1 credit)"
                    }
                  >
                    {r.busy ? "Testing…" : r.status ? "Re-test" : "Test"}
                  </button>
                  <button onClick={() => approve(i)} disabled={busySave === r.email} className={`!text-xs !py-1 !px-2.5 ${r.status === "valid" ? "btn-primary" : "btn-secondary"}`} title="Save this as the contact's primary email (other candidates are kept)">
                    {busySave === r.email ? "Saving…" : "Approve"}
                  </button>
                  <button onClick={() => removeRow(i)} className="btn-ghost !text-xs !py-1 !px-2" title="Remove this candidate from the list (does not affect the DB until you Approve)">×</button>
                </div>

                {/* Domain already answered — spending another credit here buys nothing. */}
                {blocker && (
                  <div className={`mt-2 rounded px-2.5 py-1.5 text-[11px] ${hardBlocked ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-800"}`}>
                    <strong>{domainOf(r.email)}</strong> already answered <strong>{blocker}</strong>
                    {hardBlocked
                      ? " — a catch-all domain accepts every address, so no test here can confirm this mailbox. Testing is disabled; decide manually or Approve."
                      : " — that server usually refuses to answer for any address, so a test here will most likely return unknown too."}
                  </div>
                )}

                {/* NeverBounce result detail — shown after a Test */}
                {(r.status || r.error) && (
                  <div className="mt-2 rounded bg-slate-50 px-2.5 py-2 text-[11px] text-slate-600">
                    <div><span className="font-semibold text-slate-700">Status:</span> {r.status || "error"} — {r.status ? MEANING[r.status] : r.error}</div>
                    {r.requestId && <div><span className="font-semibold text-slate-700">NeverBounce request id:</span> <span className="font-mono">{r.requestId}</span></div>}
                    {r.error && r.status && <div className="text-rose-700">{r.error}</div>}
                    {r.raw != null && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-slate-500">raw NeverBounce response</summary>
                        <pre className="mt-1 max-h-40 overflow-auto rounded bg-white p-2 text-[10px] leading-tight text-slate-700">{JSON.stringify(r.raw, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 rounded-md border border-dashed border-slate-300 p-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Add a manual email (if staff knows the real one)</div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="!h-8 !text-xs !py-1.5 !px-2.5 flex-1"
              value={manual}
              placeholder="real.email@company.com"
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addManual(); }}
            />
            <button className="btn-secondary !text-xs !py-1 !px-2.5" onClick={addManual}>Add to list</button>
          </div>
          <p className="mt-1.5 text-[10.5px] text-slate-500">
            A known-good address is free to Approve without testing — that spends no credits at all.
          </p>
        </div>
      </div>
    </div>
  );
}

function OriginBadge({ origin }: { origin: CandidateState["origin"] }) {
  const map = {
    primary:   { txt: "current primary", cls: "text-indigo-700" },
    suggested: { txt: "guess",           cls: "text-amber-700" },
    manual:    { txt: "manual",          cls: "text-slate-700" },
  } as const;
  const { txt, cls } = map[origin];
  return <span className={`uppercase tracking-wide ${cls}`}>{txt}</span>;
}
function ResultBadge({ result }: { result: NBStatus }) {
  const cls = {
    valid:      "pill pill-green",
    invalid:    "pill pill-rose",
    catchall:   "pill pill-amber",
    disposable: "pill pill-rose",
    unknown:    "pill pill-slate",
    error:      "pill pill-rose",
  }[result];
  return <span className={cls}>{result}</span>;
}
